<template>
  <div class="biot-savart-viz-3d">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$dB = (μ₀/4π) · (I dl × r̂ / r²)$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 渲染区 -->
      <div ref="canvasContainer" class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>当前电流 (I)</label>
            <span class="val">{{ current.toFixed(1) }} <small>A</small></span>
          </div>
          <div class="hud-item orange">
            <label>磁感应强度 (B)</label>
            <span class="val">{{ magneticB.toFixed(2) }} <small>mT</small></span>
          </div>
        </div>
        
        <div class="hand-rule-hint" v-if="shape === 'straight'">
          <i class="el-icon-info"></i> 右手螺旋定则：大拇指指向电流，四指指向磁场
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
          <div class="flex-row">
            <div class="control-group">
               <label class="s-label">选择电流构型</label>
               <el-radio-group v-model="shape" size="mini" @change="rebuildScene">
                  <el-radio-button label="straight">直导线</el-radio-button>
                  <el-radio-button label="loop">环形电流</el-radio-button>
               </el-radio-group>
            </div>
            <div class="control-group">
               <label class="s-label">电流强度 (I)</label>
               <el-slider v-model="current" :min="1" :max="12" :step="0.5" size="small"></el-slider>
            </div>
          </div>
        </div>
        
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-guide"></i>
           <p v-if="shape === 'straight'"><strong>长直导线：</strong>磁感线是以导线为圆心的同心圆，其平面与导线垂直。调节电流大小，观察磁感线的密度与旋转速度变化。</p>
           <p v-else><strong>环形电流：</strong>磁感器内部磁平稳通过圆心。内部由右手螺旋定则判定（四指绕电流，大拇指指磁场）。这也是电磁铁工作的核心原理。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export default {
  name: 'BiotSavartViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      current: 6,
      shape: 'straight',
      running: true
    }
  },
  computed: {
    displayTitle() {
      return this.knowledgePoint || '毕奥-萨伐尔定律：电流的磁场';
    },
    magneticB() {
      // Simplistic B = mu0 * I / (2 * pi * r) for straight or mu0 * I / (2 * R) for loop
      return this.shape === 'straight' ? this.current * 0.2 : this.current * 0.45;
    }
  },
  mounted() {
    this.initThree();
  },
  beforeDestroy() {
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer;
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x020617);
      
      this.camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 1, 1000);
      this.camera.position.set(40, 30, 60);
      
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(this.renderer.domElement);
      
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.8));
      const light = new THREE.DirectionalLight(0xffffff, 0.8);
      light.position.set(10, 20, 10);
      this.scene.add(light);

      this.rebuildScene();
      this.animate();
    },
    rebuildScene() {
      if (this.contentGroup) this.scene.remove(this.contentGroup);
      this.contentGroup = new THREE.Group();
      this.scene.add(this.contentGroup);

      if (this.shape === 'straight') {
        const wireGeo = new THREE.CylinderGeometry(0.8, 0.8, 60, 16);
        const wireMat = new THREE.MeshPhongMaterial({ color: 0xe11d48, emissive: 0x991b1b, emissiveIntensity: 0.2 });
        this.contentGroup.add(new THREE.Mesh(wireGeo, wireMat));

        this.rings = new THREE.Group();
        for (let i = 0; i < 4; i++) {
          const radius = 10 + i * 8;
          const ringGeo = new THREE.TorusGeometry(radius, 0.1, 12, 64);
          const ringMat = new THREE.MeshBasicMaterial({ color: 0x0ea5e9, transparent: true, opacity: 0.1 });
          const ringMesh = new THREE.Mesh(ringGeo, ringMat);
          ringMesh.rotation.x = Math.PI / 2;
          this.rings.add(ringMesh);
        }
        this.contentGroup.add(this.rings);

        // Current element dl
        const dlGeo = new THREE.CylinderGeometry(1.2, 1.2, 4, 16);
        const dlMat = new THREE.MeshPhongMaterial({ color: 0xfacc15, emissive: 0xfacc15 });
        this.dlElement = new THREE.Mesh(dlGeo, dlMat);
        this.contentGroup.add(this.dlElement);

        this.dBArrow = new THREE.ArrowHelper(new THREE.Vector3(0,0,1), new THREE.Vector3(15, 0, 0), 8, 0x10b981, 3, 1.5);
        this.contentGroup.add(this.dBArrow);
      } else {
        const torusGeo = new THREE.TorusGeometry(15, 1, 16, 64);
        const torusMat = new THREE.MeshPhongMaterial({ color: 0xe11d48, emissive: 0x991b1b, emissiveIntensity: 0.1 });
        const wire = new THREE.Mesh(torusGeo, torusMat);
        wire.rotation.x = Math.PI / 2;
        this.contentGroup.add(wire);

        const dlGeo = new THREE.SphereGeometry(1.5, 16, 16);
        const dlMat = new THREE.MeshPhongMaterial({ color: 0xfacc15, emissive: 0xfacc15 });
        this.dlElement = new THREE.Mesh(dlGeo, dlMat);
        this.contentGroup.add(this.dlElement);

        this.dBArrow = new THREE.ArrowHelper(new THREE.Vector3(0,1,0), new THREE.Vector3(0, 0, 0), 12, 0x10b981, 4, 2);
        this.contentGroup.add(this.dBArrow);
      }
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);
      
      const time = Date.now() * 0.0015;
      const speed = this.current * 0.004;

      if (this.shape === 'straight' && this.dlElement) {
        this.dlElement.position.y = Math.sin(time) * 20;
        this.dBArrow.position.y = this.dlElement.position.y;
        this.dBArrow.setLength(this.current * 1.5);
        if (this.rings) {
          this.rings.children.forEach((ring, idx) => {
            ring.rotation.z += speed / (idx + 1);
          });
        }
      } else if (this.shape === 'loop' && this.dlElement) {
         this.dlElement.position.x = Math.cos(time) * 15;
         this.dlElement.position.z = Math.sin(time) * 15;
         this.dBArrow.setLength(this.current * 1.5 + Math.sin(time*2) * 2);
      }

      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.biot-savart-viz-3d { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 280px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #0ea5e9; } &.orange { border-left: 3px solid #f97316; } } }
.hand-rule-hint { position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%); background: rgba(0,0,0,0.6); color: #fff; padding: 6px 16px; border-radius: 20px; font-size: 11px; white-space: nowrap; border: 1px solid rgba(255,255,255,0.1); }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .flex-row { display: flex; gap: 20px; align-items: flex-end; .control-group { flex: 1; .s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 4px; display: block; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 11px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
